import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, TextField } from "@mui/material";
import { actualizarReciboCredito, crearReciboCredito, fmtFecha, fmtMoney, listarRecibosCredito, tipoCambioDe } from "../services/egresosService";
import { fieldSx } from "./formStyles";
import { ComprobanteMarco } from "./ComprobanteUi";
import { SUB_COLORES } from "./coloresSub";
import ListadoTabla from "./ListadoTabla";
import usePaginado from "./usePaginado";
import EditarRegistroDialog, { columnaEditar } from "./EditarRegistroDialog";

const hoy = () => new Date().toISOString().slice(0, 10);
const Campo = ({ children, size = { xs: 12, sm: 6, md: 3 } }) => <Grid size={size}>{children}</Grid>;

const inicial = (catalogos) => ({
  banco_destino: catalogos.bancos[0], tipo_cambio: catalogos.cambio ?? "", monto: "", moneda: "Bolivianos",
  plazo: "", comprobante: "", tipo: "Contrato", fecha: hoy(),
});

// Formulario a partir de un recibo guardado (el tipo de cambio sale de Bs / USD).
const desdeFila = (r) => ({
  banco_destino: r.banco_destino || "", tipo_cambio: tipoCambioDe(r.bolivianos, r.dolares), monto: String(r.monto ?? ""),
  moneda: r.moneda === "Dolares" ? "Dolares" : "Bolivianos", plazo: r.plazo || "", comprobante: r.comprobante || "",
  tipo: r.tipo || "Contrato", fecha: fmtFecha(r.fecha),
});

// Campos del recibo, compartidos por el formulario de registro y el diálogo de edición.
// `bloqueado`: recibo conciliado, no se tocan banco, tipo de cambio, monto, moneda ni fecha.
function CamposRecibo({ form, setForm, bancos, bloqueado = false }) {
  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const opcionesBanco = form.banco_destino && !bancos.includes(form.banco_destino) ? [form.banco_destino, ...bancos] : bancos;
  const tipos = ["Contrato", "Linea de Credito", "Personal"];

  return (
    <>
      <Campo>
        <TextField select fullWidth size="small" label="Banco destino" value={form.banco_destino} onChange={set("banco_destino")} disabled={bloqueado} sx={fieldSx}>
          {opcionesBanco.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
        </TextField>
      </Campo>
      <Campo size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" label="Tipo de cambio" value={form.tipo_cambio} onChange={set("tipo_cambio")} disabled={bloqueado} sx={fieldSx} /></Campo>
      <Campo size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" label="Monto" value={form.monto} onChange={set("monto")} disabled={bloqueado} sx={fieldSx} /></Campo>
      <Campo size={{ xs: 6, md: 2 }}>
        <TextField select fullWidth size="small" label="Moneda" value={form.moneda} onChange={set("moneda")} disabled={bloqueado} sx={fieldSx}>
          <MenuItem value="Bolivianos">Bolivianos</MenuItem>
          <MenuItem value="Dolares">Dólares</MenuItem>
        </TextField>
      </Campo>
      <Campo size={{ xs: 12, md: 5 }}><TextField fullWidth size="small" label="Plazo y forma de pago" value={form.plazo} onChange={set("plazo")} sx={fieldSx} /></Campo>
      <Campo><TextField fullWidth size="small" label="Comprobante del banco" value={form.comprobante} onChange={set("comprobante")} sx={fieldSx} /></Campo>
      <Campo size={{ xs: 6, md: 2 }}>
        <TextField select fullWidth size="small" label="Garantía" value={form.tipo} onChange={set("tipo")} sx={fieldSx}>
          {!tipos.includes(form.tipo) && <MenuItem value={form.tipo}>{form.tipo}</MenuItem>}
          <MenuItem value="Contrato">Contrato</MenuItem>
          <MenuItem value="Linea de Credito">Línea de crédito</MenuItem>
          <MenuItem value="Personal">Personal</MenuItem>
        </TextField>
      </Campo>
      <Campo size={{ xs: 6, md: 2 }}><TextField fullWidth size="small" type="date" label="Fecha" value={form.fecha} onChange={set("fecha")} disabled={bloqueado} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} /></Campo>
    </>
  );
}

function EditarReciboDialog({ fila, bancos, onClose, onGuardado }) {
  const [form, setForm] = useState(() => desdeFila(fila));
  const conciliado = !!Number(fila.conciliado);
  return (
    <EditarRegistroDialog
      titulo="Editar recibo de crédito" id={fila.id} conciliado={conciliado} bloqueadosTxt="el banco, el monto, la moneda y la fecha"
      onClose={onClose} onGuardar={async () => { await actualizarReciboCredito(fila.id, form); onGuardado(); }}
    >
      <Grid container spacing={0.6}><CamposRecibo form={form} setForm={setForm} bancos={bancos} bloqueado={conciliado} /></Grid>
    </EditarRegistroDialog>
  );
}

// Recibo de crédito (espejo de guarda_credito.php → tabla recibo_credito).
export default function ReciboCreditoForm({ catalogos }) {
  const [form, setForm] = useState(() => inicial(catalogos));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [editando, setEditando] = useState(null);
  const paginado = usePaginado((q) => listarRecibosCredito(q));

  const guardar = async () => {
    setGuardando(true);
    setError("");
    setOk("");
    try {
      await crearReciboCredito(form);
      setOk("Recibo de crédito guardado correctamente.");
      setForm(inicial(catalogos));
      paginado.recargar(true);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo guardar el recibo.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
    <ComprobanteMarco titulo="Recibo de crédito" color={SUB_COLORES[2]}>
      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }}>{ok}</Alert>}
      <Grid container spacing={0.6}>
        <CamposRecibo form={form} setForm={setForm} bancos={catalogos.bancos} />

        <Grid size={{ xs: 12 }} sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" color="inherit" sx={{ borderRadius: 0 }} onClick={() => { setForm(inicial(catalogos)); setError(""); setOk(""); }}>Limpiar</Button>
          <Button size="small" variant="contained" sx={{ borderRadius: 0 }} onClick={guardar} disabled={guardando}>
            {guardando ? <CircularProgress size={16} color="inherit" /> : "Enviar"}
          </Button>
        </Grid>
      </Grid>

    </ComprobanteMarco>
    <ListadoTabla color={SUB_COLORES[2]}
      titulo="Recibos de crédito registrados" filas={paginado.items} cargando={paginado.cargando} paginado={paginado} vacio="No existen recibos registrados."
      columnas={[
        { h: "ID", render: (r) => r.id, sx: { fontWeight: 800, color: SUB_COLORES[2] } },
        { h: "Banco destino", render: (r) => r.banco_destino },
        { h: "Bolivianos", align: "right", render: (r) => fmtMoney(r.bolivianos), sx: { fontWeight: 700, color: "#1565c0" } },
        { h: "Dólares", align: "right", render: (r) => fmtMoney(r.dolares), sx: { fontWeight: 700, color: "#2e7d32" } },
        { h: "Moneda", render: (r) => r.moneda }, { h: "Plazo", render: (r) => r.plazo }, { h: "Comprobante", render: (r) => r.comprobante },
        { h: "Garantía", render: (r) => r.tipo }, { h: "Fecha", render: (r) => fmtFecha(r.fecha) }, { h: "Registrado por", render: (r) => r.usu },
        columnaEditar(setEditando, SUB_COLORES[2]),
      ]}
    />
    {editando && (
      <EditarReciboDialog
        fila={editando} bancos={catalogos.bancos} onClose={() => setEditando(null)}
        onGuardado={() => { setEditando(null); setOk(`Recibo N.º ${editando.id} actualizado.`); paginado.recargar(); }}
      />
    )}
    </>
  );
}
